import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import {
  getAppPerformanceData,
  AppPerformanceDataPoint,
  AppPerformanceMetricSet,
  AppPerformanceMetricValues
} from '../../api/admin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const POLL_INTERVAL_MS = 15000;

const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

const DashboardHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      App Performance
    </span>
  </h1>
);

const GRADE_OPTIONS = [
  { value: 'overall', label: 'Overall' },
  { value: '-1', label: 'Pre-K' },
  { value: '0', label: 'Kindergarten' },
  { value: '1', label: 'Grade 1' },
  { value: '2', label: 'Grade 2' },
  { value: '3', label: 'Grade 3' },
  { value: '4', label: 'Grade 4' },
  { value: '5', label: 'Grade 5' },
];

interface ChartConfig {
  title: string;
  yLabel: string;
  color: string;
  bgColor: string;
  getMetricSet: (dp: AppPerformanceDataPoint) => AppPerformanceMetricSet;
}

const CHARTS: ChartConfig[] = [
  {
    title: 'CQPM Improvement per 100 XP',
    yLabel: 'CQPM per 100 XP',
    color: 'rgb(99, 102, 241)',
    bgColor: 'rgba(99, 102, 241, 0.1)',
    getMetricSet: dp => dp.cqpmPer100Xp,
  },
  {
    title: 'Active Days (28-day window)',
    yLabel: 'Days',
    color: 'rgb(16, 185, 129)',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    getMetricSet: dp => dp.activeDays,
  },
  {
    title: 'XP per Active Day',
    yLabel: 'XP',
    color: 'rgb(245, 158, 11)',
    bgColor: 'rgba(245, 158, 11, 0.1)',
    getMetricSet: dp => dp.xpPerActiveDay,
  },
  {
    title: 'Active Time per Active Day',
    yLabel: 'Minutes',
    color: 'rgb(239, 68, 68)',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    getMetricSet: dp => dp.activeTimePerActiveDay,
  },
  {
    title: 'Total Time per Active Day',
    yLabel: 'Minutes',
    color: 'rgb(139, 92, 246)',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    getMetricSet: dp => dp.totalTimePerActiveDay,
  },
];

function getGradeValues(
  metricSet: AppPerformanceMetricSet,
  selectedGrade: string
): AppPerformanceMetricValues | null {
  if (selectedGrade === 'overall') {
    return metricSet.overall.studentCount > 0 ? metricSet.overall : null;
  }
  const gd = metricSet.byGrade[selectedGrade];
  return gd && gd.studentCount > 0 ? gd : null;
}

interface MetricChartProps {
  config: ChartConfig;
  dataPoints: AppPerformanceDataPoint[];
  selectedGrade: string;
}

const MetricChart: React.FC<MetricChartProps> = ({ config, dataPoints, selectedGrade }) => {
  const gradeLabel = GRADE_OPTIONS.find(g => g.value === selectedGrade)?.label || 'Overall';

  // Filter to points that have data for this metric + grade
  const filtered = dataPoints.filter(dp => {
    const vals = getGradeValues(config.getMetricSet(dp), selectedGrade);
    return vals !== null;
  });

  if (filtered.length === 0) return null;

  const chartData = {
    labels: filtered.map(dp => dp.date),
    datasets: [{
      label: `${config.title} (Median)`,
      data: filtered.map(dp => getGradeValues(config.getMetricSet(dp), selectedGrade)!.median),
      borderColor: config.color,
      backgroundColor: config.bgColor,
      tension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: true,
    }],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: {
        display: true,
        text: `${config.title} — ${gradeLabel}`,
        font: { size: 16, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dp = filtered[context.dataIndex];
            const vals = getGradeValues(config.getMetricSet(dp), selectedGrade)!;
            return [
              `Median: ${context.parsed.y.toFixed(2)}`,
              `Mean: ${vals.mean.toFixed(2)}`,
              `Students: ${vals.studentCount}`
            ];
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: config.yLabel } },
      x: { title: { display: true, text: 'Date' } },
    },
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <div style={{ height: '350px' }}>
          <Line data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

const GRADE_LABELS: Record<string, string> = {
  '-1': 'Pre-K', '0': 'K', '1': 'G1', '2': 'G2', '3': 'G3', '4': 'G4', '5': 'G5'
};

const GRADE_ORDER = ['-1', '0', '1', '2', '3', '4', '5'];

const BAR_COLORS = [
  'rgba(239, 68, 68, 0.7)',   // Pre-K
  'rgba(245, 158, 11, 0.7)',  // K
  'rgba(16, 185, 129, 0.7)',  // G1
  'rgba(59, 130, 246, 0.7)',  // G2
  'rgba(139, 92, 246, 0.7)',  // G3
  'rgba(236, 72, 153, 0.7)',  // G4
  'rgba(99, 102, 241, 0.7)',  // G5
];

const StudentsByGradeChart: React.FC<{ dataPoints: AppPerformanceDataPoint[] }> = ({ dataPoints }) => {
  if (dataPoints.length === 0) return null;

  const latest = dataPoints[dataPoints.length - 1];
  const byGrade = latest.cqpmPer100Xp.byGrade;

  const grades = GRADE_ORDER.filter(g => byGrade[g] && byGrade[g].studentCount > 0);
  if (grades.length === 0) return null;

  const total = grades.reduce((sum, g) => sum + byGrade[g].studentCount, 0);

  const chartData = {
    labels: grades.map(g => GRADE_LABELS[g] || g),
    datasets: [{
      label: 'Students',
      data: grades.map(g => byGrade[g].studentCount),
      backgroundColor: grades.map(g => BAR_COLORS[GRADE_ORDER.indexOf(g)] || BAR_COLORS[0]),
    }],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `Students by Grade — ${total} total (${latest.date})`,
        font: { size: 16, weight: 'bold' },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Student Count' }, ticks: { stepSize: 1 } },
      x: { title: { display: true, text: 'Grade' } },
    },
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <div style={{ height: '300px' }}>
          <Bar data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

export const AppPerformanceDashboard = () => {
  const { user: currentUser } = useAuth();
  const [dataPoints, setDataPoints] = useState<AppPerformanceDataPoint[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isComputing, setIsComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGrade, setSelectedGrade] = useState('overall');
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isAdmin = currentUser?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const result = await getAppPerformanceData();
      setDataPoints(result.dataPoints);
      setIsComputing(result.isComputing);
      setError(null);
      return result.isComputing;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch app performance data');
      return false;
    } finally {
      setIsFirstLoad(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    const poll = async () => {
      const stillComputing = await fetchData();
      if (!cancelled && stillComputing) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [isAdmin, fetchData]);

  if (!isAdmin) return <AccessDenied />;

  // First load
  if (isFirstLoad) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
        <div className="max-w-7xl mx-auto">
          <DashboardHeader />
          <div className="flex mb-4">
            <Link to="/admin" className="text-blue-600 hover:text-blue-800">&larr; Back to Admin Dashboard</Link>
          </div>
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-8 text-center">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-300 rounded mb-4 mx-auto w-1/2" />
                <div className="h-4 bg-gray-300 rounded" />
              </div>
              <p className="text-gray-600 mt-4">Loading app performance data...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
        <div className="max-w-7xl mx-auto">
          <DashboardHeader />
          <div className="flex mb-4">
            <Link to="/admin" className="text-blue-600 hover:text-blue-800">&larr; Back to Admin Dashboard</Link>
          </div>
          <Card className="shadow-lg border border-red-200 bg-red-50">
            <CardContent className="p-6">
              <p className="text-red-600 font-semibold">Error</p>
              <p className="text-red-800">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Check if we have any chart-renderable data for the selected grade
  const hasData = dataPoints.some(dp =>
    getGradeValues(dp.cqpmPer100Xp, selectedGrade) !== null
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-7xl mx-auto">
        <DashboardHeader />

        <div className="flex mb-4">
          <Link to="/admin" className="text-blue-600 hover:text-blue-800">&larr; Back to Admin Dashboard</Link>
        </div>

        {/* Grade selector + status */}
        <Card className="shadow-lg border border-gray-100 mb-6">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="grade-select" className="text-sm font-medium text-gray-700">Grade:</label>
              <select
                id="grade-select"
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {GRADE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {isComputing ? (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
                Updating data... (auto-refreshing every {POLL_INTERVAL_MS / 1000}s)
              </div>
            ) : (
              <span className="text-sm text-green-600">
                Data is up to date ({dataPoints.length} data points)
              </span>
            )}
          </CardContent>
        </Card>

        {hasData ? (
          <>
            {/* Student count by grade from latest data point */}
            <StudentsByGradeChart dataPoints={dataPoints} />

            {CHARTS.map(config => (
              <MetricChart
                key={config.title}
                config={config}
                dataPoints={dataPoints}
                selectedGrade={selectedGrade}
              />
            ))}

            <Card className="shadow-lg border border-gray-100 mb-6">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">About these metrics</h3>
                <p className="text-sm text-gray-600">
                  Each data point uses a 28-day rolling window. Charts show the <strong>median</strong> across
                  all eligible students. Tooltips show mean and student count. Grade is determined by the
                  tracks each student practiced (TRACK_TO_GRADE mapping), not their age grade.
                </p>
              </CardContent>
            </Card>
          </>
        ) : isComputing ? (
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="text-center py-12 text-gray-500">
                <div className="animate-pulse mb-4">
                  <div className="w-8 h-8 rounded-full bg-amber-300 mx-auto" />
                </div>
                <p className="text-lg font-medium">Computing data...</p>
                <p className="text-sm mt-2">
                  This is the first run. Data will appear automatically once computation completes.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg font-medium">No data available for this grade</p>
                <p className="text-sm mt-2">
                  Try selecting a different grade, or check back later once more students have completed assessments.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AppPerformanceDashboard;
